import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, Trash2, CalendarDays, Users, ArrowLeft, Lock } from "lucide-react";
import { api } from "@/lib/api";

const to12h = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function Admin() {
  const [passcode, setPasscode] = useState(sessionStorage.getItem("secc_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [entry, setEntry] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const hdr = (code) => ({ headers: { "X-Admin-Passcode": code } });

  const load = (code) => {
    setLoading(true);
    api
      .get("/appointments", hdr(code))
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  // Auto-login if passcode already in session
  useEffect(() => {
    if (!passcode) return;
    api
      .post("/admin/verify", {}, hdr(passcode))
      .then(() => {
        setAuthed(true);
        load(passcode);
      })
      .catch(() => {
        sessionStorage.removeItem("secc_admin");
        setPasscode("");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitPasscode = async (e) => {
    e.preventDefault();
    if (!entry.trim()) return;
    setVerifying(true);
    try {
      await api.post("/admin/verify", {}, hdr(entry.trim()));
      sessionStorage.setItem("secc_admin", entry.trim());
      setPasscode(entry.trim());
      setAuthed(true);
      load(entry.trim());
    } catch {
      toast.error("Incorrect passcode");
    } finally {
      setVerifying(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("secc_admin");
    setAuthed(false);
    setPasscode("");
    setEntry("");
    setRows([]);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/appointments/${id}`, hdr(passcode));
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Appointment removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const today = format(new Date(), "yyyy-MM-dd");
  const todayCount = rows.filter((r) => r.date === today).length;

  if (!authed) {
    return (
      <div className="min-h-screen bg-[var(--ink)] text-white flex items-center justify-center px-4">
        <form onSubmit={submitPasscode} className="w-full max-w-sm text-center" data-testid="passcode-form">
          <img src="/assets/logo.png" alt="logo" className="h-14 mx-auto bg-white rounded-xl p-2" />
          <div className="mt-8 w-14 h-14 mx-auto rounded-full bg-white/10 flex items-center justify-center text-[var(--teal-light)]">
            <Lock size={24} />
          </div>
          <h1 className="mt-6 font-display text-3xl">Staff Access</h1>
          <p className="text-white/50 text-sm mt-2">Enter the passcode to view appointments.</p>
          <input
            data-testid="passcode-input"
            type="password"
            inputMode="numeric"
            value={entry}
            onChange={(e) => setEntry(e.target.value)}
            placeholder="Passcode"
            className="mt-6 w-full text-center tracking-[0.3em] px-4 py-4 rounded-xl bg-white/5 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--teal-light)]"
          />
          <button
            data-testid="passcode-submit"
            type="submit"
            disabled={verifying}
            className="mt-4 w-full px-6 py-4 rounded-full bg-[var(--teal)] text-white font-semibold hover:bg-[var(--teal-dark)] transition disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {verifying ? <Loader2 className="animate-spin" size={18} /> : "Unlock"}
          </button>
          <a href="/" className="mt-6 inline-flex items-center gap-2 text-sm text-white/50 hover:text-white">
            <ArrowLeft size={16} /> Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ink)] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="logo" className="h-11 bg-white rounded-lg p-1.5" />
            <div>
              <h1 className="font-display text-3xl">Appointments</h1>
              <p className="text-white/50 text-sm">Shah Eye Care Centre · Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <button data-testid="logout-btn" onClick={logout} className="text-sm text-white/60 hover:text-white">Lock</button>
            <a data-testid="back-home" href="/" className="flex items-center gap-2 text-sm text-white/60 hover:text-white">
              <ArrowLeft size={16} /> Back to site
            </a>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <Users className="text-[var(--teal-light)] mb-3" />
            <p className="text-4xl font-semibold tabular-nums" data-testid="stat-total">{rows.length}</p>
            <p className="text-white/50 text-sm">Total appointments</p>
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <CalendarDays className="text-[var(--teal-light)] mb-3" />
            <p className="text-4xl font-semibold tabular-nums" data-testid="stat-today">{todayCount}</p>
            <p className="text-white/50 text-sm">Today</p>
          </div>
        </div>

        <div className="mt-8 rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center gap-2 p-10 text-white/60"><Loader2 className="animate-spin" size={18} /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-white/50" data-testid="empty-state">No appointments yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="appointments-table">
                <thead>
                  <tr className="text-left text-white/40 border-b border-white/10">
                    <th className="px-5 py-4 font-medium">Patient</th>
                    <th className="px-5 py-4 font-medium">Age / Sex</th>
                    <th className="px-5 py-4 font-medium">Date</th>
                    <th className="px-5 py-4 font-medium">Slot</th>
                    <th className="px-5 py-4 font-medium">Phone</th>
                    <th className="px-5 py-4 font-medium">Address</th>
                    <th className="px-5 py-4 font-medium">Payment</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`row-${r.id}`} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-5 py-4 font-medium">{r.patient_name}</td>
                      <td className="px-5 py-4 text-white/70">{r.age} · {r.sex}</td>
                      <td className="px-5 py-4 text-white/70">{format(new Date(r.date), "dd MMM yyyy")}</td>
                      <td className="px-5 py-4"><span className="px-2.5 py-1 rounded-full bg-[var(--teal)]/25 text-[var(--teal-light)]">{to12h(r.time_slot)}</span></td>
                      <td className="px-5 py-4 text-white/70">{r.phone || "—"}</td>
                      <td className="px-5 py-4 text-white/60 max-w-[220px] truncate">{r.address}</td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs ${r.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>
                          ₹{r.fee} {r.payment_status === "paid" ? "Paid" : "Pending"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <button data-testid={`delete-${r.id}`} onClick={() => remove(r.id)} className="text-white/40 hover:text-red-400 transition">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
